import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from '@react-router';
import { addError } from '@shared/utils/logger';
import { withWindow } from '@shared/utils/withWindow';

interface DurationCallback {
  duration: number;
  callback: () => void;
  callbackName: string;
}

interface UsePageDurationTimerOptions {
  durations: DurationCallback[];
  enabled?: boolean;
}

// Helper function to check if a path is a subpath of another
const isSubpath = (parentPath: string, childPath: string): boolean => {
  // Special case: root path should never be treated as a subpath of anything
  if (parentPath === '/') {
    return false;
  }

  // Normalize paths by ensuring they start with / and removing trailing slashes
  const normalizePath = (path: string): string => {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return normalized.replace(/\/$/, '');
  };

  const normalizedParent = normalizePath(parentPath);
  const normalizedChild = normalizePath(childPath);

  // Check if child path equals parent path or starts with parent path followed by a slash
  return normalizedChild === normalizedParent || normalizedChild.startsWith(normalizedParent + '/');
};

export const usePageDurationTimer = ({ durations, enabled = true }: UsePageDurationTimerOptions) => {
  const location = useLocation();
  const timersRef = useRef<number[]>([]);
  const previousPathRef = useRef<string>('');

  const clearAllTimers = useCallback(() => {
    withWindow(global => {
      timersRef.current.forEach(timer => global.clearTimeout(timer));
      timersRef.current = [];
    });
  }, []);

  const startTimers = useCallback(() => {
    withWindow(global => {
      durations.forEach(({ duration, callback, callbackName }) => {
        const timer = global.setTimeout(() => {
          try {
            callback();
          } catch (error) {
            addError(error, { context: 'usePageDurationTimer', duration, callback: callbackName });
          }
        }, duration);

        timersRef.current.push(timer);
      });
    });
  }, [durations]);

  // Start timers on mount and whenever path changes to a different section
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    const shouldReset = !previousPath || !isSubpath(previousPath, currentPath);

    if (shouldReset) {
      clearAllTimers();

      if (enabled) {
        startTimers();
      }
    }

    // Update the previous path reference
    previousPathRef.current = currentPath;
  }, [location.pathname, enabled, clearAllTimers, startTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return clearAllTimers;
  }, [clearAllTimers]);
};
